import { PlusOutlined, DownloadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Drawer, PageHeader, Table } from 'antd';
import React from 'react';
import { DRAWER_WIDTH } from '../../config/constants';
import Routes from '../../config/routes';
import { translations } from '../../config/translations';
import { ActionType, DrawerState, ModuleName } from '../../core/models/enum';
import { authorizeAction } from '../../helpers/CheckPermissionHelper';
import { getNewSubRoute } from '../../helpers/RoutingHelper';
import { FormTemplatesClient, FormTemplateVm } from '../../utils/api';
import { remove } from '../../helpers/SubmitHelper';
import { showConfirm } from '../../helpers/NotificationHelper';
import { Props } from './index';
import FormTemplatesForm from './form-templates-form';

interface State {
    allowCreate: boolean;
    drawerState: DrawerState;
    items?: FormTemplateVm[];
    editing?: FormTemplateVm;
}

class FormTemplates extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);

        const {
            location: { pathname },
        } = window;

        this.state = {
            allowCreate: authorizeAction(
                props.userProfile,
                ModuleName.FormTemplates,
                ActionType.Create
            ),
            drawerState:
                pathname === Routes.ROUTE_FORM_TEMPLATES_NEW
                    ? DrawerState.Edit
                    : DrawerState.Closed,
            items: [],
        };
    }

    public componentDidMount = () => {
        this.getItems();
    };

    private getItems = async () => {
        const items = await new FormTemplatesClient().getAll(undefined, undefined);
        this.setState({ items });
    };

    private handleDrawerMode = (drawerState: DrawerState) => {
        const { history } = this.props;
        // Clear editing when closing or switching to create mode to avoid stale data
        const shouldClearEditing =
            drawerState === DrawerState.Closed || drawerState === DrawerState.Create;
        this.setState({
            drawerState,
            editing: shouldClearEditing ? undefined : this.state.editing,
        });
        history.push(
            getNewSubRoute(
                Routes.ROUTE_FORM_TEMPLATES,
                Routes.ROUTE_FORM_TEMPLATES,
                Routes.ROUTE_FORM_TEMPLATES_EDIT,
                Routes.ROUTE_FORM_TEMPLATES_NEW,
                drawerState
            )
        );
    };

    private handleSuccessCreate = () => {
        this.handleDrawerMode(DrawerState.Closed);
        this.getItems();
    };

    private handleCreate = () => {
        // Ensure previous editing record is cleared before opening create form
        this.setState({ editing: undefined }, () => this.handleDrawerMode(DrawerState.Create));
    };

    private handleDelete = async (id: number) => {
        await remove(
            new FormTemplatesClient() as any,
            translations.formTemplates.deleteSuccess,
            () => this.getItems(),
            id
        );
    };

    private handleDownload = async (id: number) => {
        const file = await new FormTemplatesClient().downloadFile(id);
        const url = window.URL.createObjectURL(file.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.fileName || 'file';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    public render(): React.ReactElement {
        const { allowCreate, drawerState, items, editing } = this.state;

        return (
            <>
                <PageHeader
                    title={translations.formTemplates.title}
                    extra={
                        allowCreate
                            ? [
                                  <Button
                                      key="1"
                                      type="primary"
                                      onClick={() => this.handleCreate()}
                                      className="no-print"
                                  >
                                      <PlusOutlined /> {translations.formTemplates.add}
                                  </Button>,
                              ]
                            : []
                    }
                />

                <Table
                    rowKey={(r) => r.id!.toString()}
                    dataSource={items}
                    pagination={{ pageSize: 20 }}
                    columns={[
                        { title: translations.formTemplates.fileName, dataIndex: 'fileName' },
                        { title: translations.formTemplates.remark, dataIndex: 'remark' },
                        { title: translations.formTemplates.createdBy, dataIndex: 'createdBy' },
                        {
                            title: translations.formTemplates.createdDate,
                            dataIndex: 'createdDate',
                            render: (v: any) => new Date(v).toLocaleString(),
                        },
                        {
                            title: translations.general.actions,
                            render: (_, record: FormTemplateVm) => (
                                <>
                                    <DownloadOutlined
                                        onClick={() => this.handleDownload(record.id!)}
                                        className="blue-6"
                                    />
                                    {allowCreate && (
                                        <>
                                            <EditOutlined
                                                style={{ marginLeft: 10 }}
                                                onClick={() =>
                                                    this.setState({ editing: record }, () =>
                                                        this.handleDrawerMode(DrawerState.Edit)
                                                    )
                                                }
                                            />
                                            <DeleteOutlined
                                                className="red-5"
                                                style={{ marginLeft: 10 }}
                                                onClick={() =>
                                                    showConfirm(
                                                        () => this.handleDelete(record.id!),
                                                        translations.formTemplates.deleteConfirm,
                                                        translations.general.delete,
                                                        true
                                                    )
                                                }
                                            />
                                        </>
                                    )}
                                </>
                            ),
                        },
                    ]}
                />

                <Drawer
                    title={
                        editing ? translations.formTemplates.edit : translations.formTemplates.add
                    }
                    open={!!drawerState}
                    onClose={() => this.handleDrawerMode(DrawerState.Closed)}
                    width={DRAWER_WIDTH}
                    destroyOnClose
                >
                    <FormTemplatesForm
                        onClose={() => this.handleDrawerMode(DrawerState.Closed)}
                        onSuccess={this.handleSuccessCreate}
                        formTemplate={editing}
                    />
                </Drawer>
            </>
        );
    }
}

export default FormTemplates;
